/**
 * Where every footnote lands.
 *
 * One entry per cited metric, in the order the page cites them, each carrying
 * the formula, the inputs it actually depends on with their values and sources,
 * and what it does not capture. The dependency list is derived by walking the
 * expression rather than written by hand, so it cannot drift from the number.
 *
 * Adjustable inputs get their slider here rather than next to the figure. A
 * reader who wants to argue with an assumption has come to argue; a reader
 * looking at the headline should not have to step over a control to read it.
 */

import { evaluateMetric, parseCached } from '../engine/evaluate.js';
import { provenanceOf } from '../engine/provenance.js';
import type { Metric, Pack, Range } from '../engine/schema.js';
import { isRange, isUndisclosed } from '../engine/schema.js';
import { buildScope } from '../engine/scope.js';
import { formatValue, roundToSigFigs } from '../engine/units.js';
import type { Footnotes } from './footnotes.js';
import { esc, formatMid } from './num.js';
import type { AppState } from './store.js';

export function assumptions(pack: Pack, state: AppState, fn: Footnotes): string {
  const entries = fn.entries();
  if (!entries.length) return '';

  const scope = buildScope(pack, {
    scenarioId: state.scenarioId,
    params: {},
    overrides: state.overrides,
  });
  const sourceById = Object.fromEntries(pack.sources.map((s) => [s.id, s]));

  const blocks = entries
    .map(({ n, id }) => {
      const metric = pack.metrics.find((m) => m.id === id);
      if (!metric) return '';
      return assumptionBlock(pack, state, n, metric, scope, sourceById);
    })
    .join('');

  return `<section class="assumptions" id="assumptions"><div class="container">
    <h2 class="section-title">Assumptions, formulas and sources</h2>
    <p class="lede">Each numbered figure appears here with the arithmetic that produced it, the inputs it depends on, where each one came from, and what it does not capture. Adjustable assumptions can be moved.</p>
    ${blocks}
  </div></section>`;
}

function assumptionBlock(
  pack: Pack,
  state: AppState,
  n: number,
  metric: Metric,
  scope: ReturnType<typeof buildScope>,
  sourceById: Record<string, Pack['sources'][number]>,
): string {
  const result = evaluateMetric(pack, metric, {
    scenarioId: state.scenarioId,
    params: {},
    overrides: state.overrides,
  });
  const prov = provenanceOf(parseCached(metric.expr), pack);

  const value = result.blocked
    ? 'not publicly disclosed'
    : formatMid(result.value as Range, result.unit, result.sigFigs);

  const rows = prov.inputIds
    .map((id) => {
      const input = pack.inputs.find((i) => i.id === id);
      if (!input) return '';
      const v = scope.values[id];
      const shown = isUndisclosed(input.value)
        ? 'not disclosed'
        : v === undefined
          ? '—'
          : isRange(v)
            ? `${formatValue(roundToSigFigs(v.mid, input.sigFigs), input.unit)} (${formatValue(
                roundToSigFigs(v.lo, input.sigFigs),
                input.unit,
              )}–${formatValue(roundToSigFigs(v.hi, input.sigFigs), input.unit)})`
            : formatValue(roundToSigFigs(v, input.sigFigs), input.unit);

      const cites = input.sourceIds
        .map((sid) => sourceById[sid])
        .filter(Boolean)
        .map(
          (s) =>
            `<a href="${esc(s!.url)}" target="_blank" rel="noopener">${esc(s!.publisher)}</a>`,
        )
        .join(', ');

      const modified = scope.modified.includes(id);

      return `<tr class="${modified ? 'is-modified' : ''}">
        <th scope="row">${esc(input.label)}</th>
        <td class="num mono">${esc(shown)}</td>
        <td>${esc(input.kind)} · ${esc(input.confidence)}</td>
        <td>${cites || '—'}</td>
      </tr>
      ${
        input.adjustable
          ? `<tr class="slider-row"><td colspan="4">
              <label for="s-${esc(metric.id)}-${esc(id)}">${esc(input.adjustable.sliderLabel)}</label>
              <input id="s-${esc(metric.id)}-${esc(id)}" type="range"
                min="${input.adjustable.min}" max="${input.adjustable.max}"
                step="${input.adjustable.step}"
                value="${typeof v === 'number' ? v : (v?.mid ?? input.adjustable.min)}"
                data-override="${esc(id)}" />
              ${modified ? `<button class="assumption-reset" data-reset-override="${esc(id)}">reset</button>` : ''}
              <span class="slider-why">${esc(input.adjustable.rationale)}</span>
            </td></tr>`
          : ''
      }
      ${
        input.contested?.length
          ? `<tr class="contested-row"><td colspan="4">
              ${input.contested
                .map(
                  (c) =>
                    `<p class="contested"><strong>Disputed.</strong> ${esc(c.claim)} <em>${esc(
                      c.counterClaim,
                    )}</em></p>`,
                )
                .join('')}
            </td></tr>`
          : ''
      }`;
    })
    .join('');

  return `<article class="assumption" id="assumption-${esc(metric.id)}">
    <header class="assumption-head">
      <span class="assumption-n">${n}</span>
      <div>
        <h3>${esc(metric.question)}</h3>
        <p class="assumption-value mono">${esc(value)} <span>${esc(metric.label)}</span></p>
      </div>
      <a class="assumption-back" href="#fn-${esc(metric.id)}" aria-label="Back to where this is cited">back to the figure</a>
    </header>

    <p class="formula mono">${esc(metric.expr)}</p>
    <p class="prose">${esc(metric.methodology)}</p>

    <table class="assumption-table">
      <thead><tr><th>Input</th><th>Value</th><th>Kind</th><th>Source</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>

    <p class="eyebrow">What this does not capture</p>
    <ul class="caveat-list">${metric.caveats.map((c) => `<li>${esc(c)}</li>`).join('')}</ul>

    <button class="assumption-reset" data-copy-citation="${esc(metric.id)}">Copy this figure with its sources</button>
  </article>`;
}
